/**
 * "What's next?" section: markdown content + Email / LinkedIn CTAs.
 * When asFauxFooter is true, styled as the page footer (gradient, border).
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from './ui/button';
import { APP_CONFIG } from '../constants';

interface HelpBlockProps {
  content: string;
  /** When true, styles this section as the page footer (no real footer below). */
  asFauxFooter?: boolean;
}

export const HelpBlock: React.FC<HelpBlockProps> = ({ content, asFauxFooter }) => {
  if (!content.trim()) return null;

  return (
    <section
      id="need-help"
      className={asFauxFooter
        ? 'w-full py-16 pb-20 px-6 bg-gradient-to-b from-blue-50/40 to-slate-50/80 border-t border-slate-200/80'
        : 'w-full py-16 px-6 bg-blue-50/40'
      }
      aria-labelledby="need-help-title"
    >
      <div className="max-w-container mx-auto">
        <h2
          id="need-help-title"
          className="text-xl font-semibold text-text-primary mb-6"
        >
          What&apos;s next?
        </h2>
        <div className="prose prose-slate max-w-none [&>:first-child]:mt-0 [&>:last-child]:mb-0 need-help-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </div>
        <div className="mt-10 pt-8 border-t border-slate-200/80 flex flex-col sm:flex-row gap-3">
          <Button asChild variant="default" size="default">
            <a
              href={`mailto:${APP_CONFIG.author.email}?subject=Design Systems for AI Help`}
              aria-label="Email for guidance"
            >
              Email {APP_CONFIG.author.name}
            </a>
          </Button>
          <Button asChild variant="outline" size="default">
            <a
              href={APP_CONFIG.author.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Connect on LinkedIn"
            >
              LinkedIn
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};
